import type { Vec2 } from '../../../../lib/simulation-core'
import type { VehicleConfig, VehicleState } from './types'
import { excitatory, inhibitory, bellCurve, threshold } from './response'

interface VehiclePreset {
  config: VehicleConfig
  initialState: VehicleState
}

function makeState(
  position?: Vec2,
  heading?: number,
): VehicleState {
  return {
    position: position ?? { x: 0, y: 0 },
    heading: heading ?? 0,
    leftMotor: 0,
    rightMotor: 0,
    sensorStimuli: [],
    primaryStimulus: 0,
    trail: [],
  }
}

const SENSOR_AHEAD = 4
const SENSOR_SPREAD = 5
const WHEEL_BASE = 10

// --- Vehicle 1: one sensor, one motor, Brownian noise ---

export function vehicle1(position?: Vec2, heading?: number): VehiclePreset {
  return {
    config: {
      sensors: [{ offset: { x: SENSOR_AHEAD, y: 0 } }],
      connections: [
        { sensorIndex: 0, motorIndex: 0, response: excitatory(80) },
      ],
      wheelBase: WHEEL_BASE,
      driveMode: 'single-forward',
      color: '#71675f',
      darkColor: '#71675f',
      label: '1',
      brownianNoise: 8,
    },
    initialState: makeState(position, heading),
  }
}

// --- Vehicle 2a: uncrossed excitatory — COWARD ---

export function vehicle2a(position?: Vec2, heading?: number): VehiclePreset {
  return {
    config: {
      sensors: [
        { offset: { x: SENSOR_AHEAD, y: -SENSOR_SPREAD } },
        { offset: { x: SENSOR_AHEAD, y: SENSOR_SPREAD } },
      ],
      connections: [
        { sensorIndex: 0, motorIndex: 0, response: excitatory(120) },
        { sensorIndex: 1, motorIndex: 1, response: excitatory(120) },
      ],
      wheelBase: WHEEL_BASE,
      driveMode: 'differential',
      color: '#516d84',
      darkColor: '#516d84',
      label: '2a',
      brownianNoise: 0,
    },
    initialState: makeState(position, heading),
  }
}

// --- Vehicle 2b: crossed excitatory — AGGRESSIVE ---

export function vehicle2b(position?: Vec2, heading?: number): VehiclePreset {
  return {
    config: {
      sensors: [
        { offset: { x: SENSOR_AHEAD, y: -SENSOR_SPREAD } },
        { offset: { x: SENSOR_AHEAD, y: SENSOR_SPREAD } },
      ],
      connections: [
        { sensorIndex: 0, motorIndex: 1, response: excitatory(120) },
        { sensorIndex: 1, motorIndex: 0, response: excitatory(120) },
      ],
      wheelBase: WHEEL_BASE,
      driveMode: 'differential',
      color: '#9b574c',
      darkColor: '#9b574c',
      label: '2b',
      brownianNoise: 0,
    },
    initialState: makeState(position, heading),
  }
}

// --- Vehicle 3a: uncrossed inhibitory — LOVE ---

export function vehicle3a(position?: Vec2, heading?: number): VehiclePreset {
  return {
    config: {
      sensors: [
        { offset: { x: SENSOR_AHEAD, y: -SENSOR_SPREAD } },
        { offset: { x: SENSOR_AHEAD, y: SENSOR_SPREAD } },
      ],
      connections: [
        { sensorIndex: 0, motorIndex: 0, response: inhibitory(100, 80) },
        { sensorIndex: 1, motorIndex: 1, response: inhibitory(100, 80) },
      ],
      wheelBase: WHEEL_BASE,
      driveMode: 'differential',
      color: '#566d58',
      darkColor: '#566d58',
      label: '3a',
      brownianNoise: 0,
    },
    initialState: makeState(position, heading),
  }
}

// --- Vehicle 3b: crossed inhibitory — EXPLORER ---

export function vehicle3b(position?: Vec2, heading?: number): VehiclePreset {
  return {
    config: {
      sensors: [
        { offset: { x: SENSOR_AHEAD, y: -SENSOR_SPREAD } },
        { offset: { x: SENSOR_AHEAD, y: SENSOR_SPREAD } },
      ],
      connections: [
        { sensorIndex: 0, motorIndex: 1, response: inhibitory(100, 80) },
        { sensorIndex: 1, motorIndex: 0, response: inhibitory(100, 80) },
      ],
      wheelBase: WHEEL_BASE,
      driveMode: 'differential',
      color: '#71675f',
      darkColor: '#71675f',
      label: '3b',
      brownianNoise: 0,
    },
    initialState: makeState(position, heading),
  }
}

// --- Vehicle 4a: crossed bell curve — orbiter ---

export function vehicle4a(position?: Vec2, heading?: number): VehiclePreset {
  return {
    config: {
      sensors: [
        { offset: { x: SENSOR_AHEAD, y: -SENSOR_SPREAD } },
        { offset: { x: SENSOR_AHEAD, y: SENSOR_SPREAD } },
      ],
      connections: [
        { sensorIndex: 0, motorIndex: 1, response: bellCurve(0.3, 0.15, 100) },
        { sensorIndex: 1, motorIndex: 0, response: bellCurve(0.3, 0.15, 100) },
      ],
      wheelBase: WHEEL_BASE,
      driveMode: 'differential',
      color: '#80612f',
      darkColor: '#80612f',
      label: '4a',
      brownianNoise: 0.6,
    },
    initialState: makeState(position, heading),
  }
}

// --- Vehicle 4b: crossed threshold ---

export function vehicle4b(position?: Vec2, heading?: number): VehiclePreset {
  return {
    config: {
      sensors: [
        { offset: { x: SENSOR_AHEAD, y: -SENSOR_SPREAD } },
        { offset: { x: SENSOR_AHEAD, y: SENSOR_SPREAD } },
      ],
      connections: [
        { sensorIndex: 0, motorIndex: 1, response: threshold(0.2, 100) },
        { sensorIndex: 1, motorIndex: 0, response: threshold(0.2, 100) },
      ],
      wheelBase: WHEEL_BASE,
      driveMode: 'differential',
      color: '#80612f',
      darkColor: '#80612f',
      label: '4b',
      brownianNoise: 0,
    },
    initialState: makeState(position, heading),
  }
}
